// Blog.tsx - Enhanced blog page with categories, featured posts, and improved display
// Fetches and displays blog posts from Firestore (Firebase v9 modular API)

import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  QuerySnapshot,
  DocumentData,
  limit,
  where,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import '../styles/Blog.css';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image: string;
  link: string;
  featured?: boolean;
  mentionedLinks?: Array<{
    url: string;
    title: string;
  }>;
}

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'cybersecurity', name: 'Cybersecurity' },
    { id: 'bourbon', name: 'Bourbon' },
    { id: 'industry', name: 'Industry News' }
  ];

  const blogPosts: BlogPost[] = [
    {
      id: '1',
      title: 'The Evolution of Ransomware: A Deep Dive into Modern Threats',
      excerpt: 'Explore how ransomware has evolved from simple encryption attacks to sophisticated operations targeting critical infrastructure and supply chains.',
      author: 'Brian Krebs',
      date: '2024-03-15',
      category: 'cybersecurity',
      readTime: '8 min read',
      image: 'https://krebsonsecurity.com/wp-content/uploads/2024/03/ransomware-evolution.jpg',
      link: 'https://krebsonsecurity.com/2024/03/the-evolution-of-ransomware/',
      featured: true,
      mentionedLinks: [
        {
          url: 'https://www.cisa.gov/ransomware',
          title: 'CISA Ransomware Guide'
        },
        {
          url: 'https://www.fbi.gov/investigate/cyber',
          title: 'FBI Cyber Crime Division'
        }
      ]
    },
    {
      id: '2',
      title: 'Kentucky Bourbon Trail: A Journey Through America\'s Whiskey Heritage',
      excerpt: 'Discover the rich history and craftsmanship behind Kentucky\'s most famous distilleries and their impact on the bourbon industry.',
      author: 'Fred Minnick',
      date: '2024-03-10',
      category: 'bourbon',
      readTime: '6 min read',
      image: 'https://fredminnick.com/wp-content/uploads/2024/03/kentucky-bourbon-trail.jpg',
      link: 'https://fredminnick.com/2024/03/kentucky-bourbon-trail/',
      mentionedLinks: [
        {
          url: 'https://kybourbontrail.com/',
          title: 'Official Kentucky Bourbon Trail'
        }
      ]
    },
    {
      id: '3',
      title: 'Zero Trust Security: Moving Beyond Traditional Perimeter Defense',
      excerpt: 'Learn how organizations are implementing zero trust architecture to protect against modern cyber threats and data breaches.',
      author: 'Bruce Schneier',
      date: '2024-03-08',
      category: 'cybersecurity',
      readTime: '10 min read',
      image: 'https://www.schneier.com/wp-content/uploads/2024/03/zero-trust.jpg',
      link: 'https://www.schneier.com/blog/archives/2024/03/zero-trust-security.html',
      mentionedLinks: [
        {
          url: 'https://www.nist.gov/publications/zero-trust-architecture',
          title: 'NIST Zero Trust Architecture'
        }
      ]
    },
    {
      id: '4',
      title: 'Understanding Bourbon Mash Bills: The Foundation of Flavor',
      excerpt: 'Dive into the science behind bourbon mash bills and how different grain combinations create unique flavor profiles in your favorite spirits.',
      author: 'Jim Murray',
      date: '2024-03-05',
      category: 'bourbon',
      readTime: '7 min read',
      image: 'https://www.whiskyadvocate.com/wp-content/uploads/2024/03/mash-bills.jpg',
      link: 'https://www.whiskyadvocate.com/mash-bills-guide/'
    },
    {
      id: '5',
      title: 'Supply Chain Security: Protecting the Digital Pipeline',
      excerpt: 'An in-depth look at supply chain security challenges and best practices for protecting your organization\'s digital infrastructure.',
      author: 'Dan Kaminsky',
      date: '2024-03-01',
      category: 'cybersecurity',
      readTime: '9 min read',
      image: 'https://www.darkreading.com/wp-content/uploads/2024/03/supply-chain.jpg',
      link: 'https://www.darkreading.com/supply-chain-security'
    },
    {
      id: '6',
      title: 'Bourbon Barrel Selection: The Art of Choosing the Perfect Cask',
      excerpt: 'Learn how master distillers and blenders select and manage barrels to create exceptional bourbon expressions with consistent quality.',
      author: 'Chuck Cowdery',
      date: '2024-02-28',
      category: 'bourbon',
      readTime: '5 min read',
      image: 'https://chuckcowdery.blogspot.com/2024/02/barrel-selection.jpg',
      link: 'https://chuckcowdery.blogspot.com/2024/02/bourbon-barrel-selection.html'
    }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <div className="blog-page">
      <div className="blog-header">
        <h1>Sip'N'Security Blog</h1>
        <p className="blog-description">
          Explore the intersection of cybersecurity and bourbon culture through our curated collection of industry insights, 
          expert analysis, and premium whiskey reviews.
        </p>
      </div>

      <div className="category-filter">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      {featuredPost && (
        <div className="featured-post">
          <div className="featured-content">
            <div className="featured-text">
              <span className="featured-label">Featured Article</span>
              <h2>{featuredPost.title}</h2>
              <div className="featured-meta">
                <span className="featured-author">By {featuredPost.author}</span>
                <span className="featured-date">{featuredPost.date}</span>
                <span className="featured-read-time">{featuredPost.readTime}</span>
              </div>
              <p className="featured-excerpt">{featuredPost.excerpt}</p>
              <a 
                href={featuredPost.link}
                target="_blank"
                rel="noopener noreferrer"
                className="featured-btn"
              >
                Read Full Article
              </a>
            </div>
            <div className="featured-image">
              <iframe
                src={featuredPost.link}
                title={featuredPost.title}
                className="featured-iframe"
                loading="lazy"
                sandbox="allow-same-origin allow-scripts"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
        </div>
      )}

      <div className="blog-posts">
        {regularPosts.map(post => (
          <article key={post.id} className="blog-post">
            {post.mentionedLinks && post.mentionedLinks.length > 0 && (
              <ul className="article-links">
                {post.mentionedLinks.map(link => (
                  <li key={link.url}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">{link.title}</a>
                  </li>
                ))}
              </ul>
            )}
            <div className="post-meta">
              <span className="post-category">{post.category}</span>
              <span className="published-date">{post.date}</span>
              <span className="read-time">{post.readTime}</span>
            </div>
            <h3>{post.title}</h3>
            <p className="post-author">By {post.author}</p>
            <div className="post-image">
              <iframe
                src={post.link}
                title={post.title}
                className="post-iframe"
                loading="lazy"
                sandbox="allow-same-origin allow-scripts"
                referrerPolicy="no-referrer"
              />
            </div>
            <p className="post-excerpt">{post.excerpt}</p>
            <a 
              href={post.link}
              target="_blank"
              rel="noopener noreferrer"
              className="read-more-btn"
            >
              Read More
            </a>
          </article>
        ))}
      </div>
    </div>
  );
};

export default Blog;
